// Display logic for the notification bell. Pure and dependency-free so it can
// be unit-tested directly, in the same spirit as ranking.js and featured.js.
//
// The server writes notification ROWS (see the notification triggers in
// functions/index.js); everything here is about turning one into a sentence,
// a link, and a timestamp.

// Kept in step with NOTIFICATION_TYPES in functions/pure.js. An unknown type
// is rendered with a generic sentence rather than dropped, so a row written
// by a newer server than the loaded client still shows up as something.
export const NOTIFICATION_TYPES = ['rating', 'follow', 'comment'];

// The one-line sentence for a notification, given the actor's CURRENT display
// name -- joined at render, never stored on the row (see the note on the
// notification triggers, and getComments for the same call). 'Someone' is the
// fallback for an actor whose profile is private, deleted, or otherwise
// unreadable: a real outcome, not a defensive nicety.
export function notificationText(notification, actorName) {
  const who = actorName || 'Someone';
  const what = notification?.recipeTitle
    ? `“${notification.recipeTitle}”`
    : 'your recipe';

  switch (notification?.type) {
    case 'follow':
      return `${who} started following you`;
    case 'comment':
      return `${who} commented on ${what}`;
    case 'rating':
      return `${who} gave ${what} 5 stars`;
    default:
      return `${who} interacted with your recipes`;
  }
}

// Where clicking a notification should go. A follow points at the person; the
// rest point at the recipe. Returns null when the row lacks what it would
// need, so the caller can render it as plain text rather than a dead link.
export function notificationLink(notification) {
  if (!notification) return null;
  if (notification.type === 'follow') {
    return notification.actorId ? `/user/${notification.actorId}` : null;
  }
  return notification.recipeId ? `/recipe/${notification.recipeId}` : null;
}

// The emoji shown alongside a notification when the actor has no avatar --
// and as a type marker regardless. Matches the vocabulary already used in the
// navbar and on recipe cards.
export function notificationIcon(type) {
  if (type === 'follow') return '👤';
  if (type === 'comment') return '💬';
  if (type === 'rating') return '⭐';
  return '🔔';
}

// Firestore timestamps arrive in three shapes depending on where the row came
// from: a real Timestamp, a plain {seconds} object from a cache hit, or null
// while a serverTimestamp() write is still pending locally. Normalize all
// three -- the same defensive read getComments and rankCurated already do.
export function timestampMillis(value) {
  if (!value) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  return null;
}

// Compact relative time for a dense list: "just now", "4m", "3h", "2d", "5w".
// Deliberately short and unsuffixed past the first bucket -- in a narrow
// panel, "ago" on every row is noise, and the column reads as a scale.
// Anything older than a year stops counting and says "1y+", since precision
// there tells the reader nothing.
export function relativeTime(millis, now = Date.now()) {
  if (millis == null) return '';
  const seconds = Math.floor((now - millis) / 1000);
  // Clock skew between the browser and the server can make a fresh row look
  // slightly future-dated. Read that as "just now" rather than a negative.
  if (seconds < 45) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 52) return `${weeks}w`;
  return '1y+';
}

// How many unread rows to claim on the badge. Past the cap the exact number
// stops being actionable, and the listener only holds NOTIFICATION_LIMIT rows
// anyway -- so counting higher would be a lie, not just clutter.
export const BADGE_CAP = 9;

export function badgeLabel(unreadCount) {
  if (!unreadCount || unreadCount < 1) return '';
  return unreadCount > BADGE_CAP ? `${BADGE_CAP}+` : String(unreadCount);
}
